"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = AIChatPanel;
var _react = require("react");
var _recharts = require("recharts");
var _reactMarkdown = _interopRequireDefault(require("react-markdown"));
var _remarkGfm = _interopRequireDefault(require("remark-gfm"));
var _lucideReact = require("lucide-react");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// Default theme — apps can override via the `theme` prop.
const DEFAULT_THEME = {
  navy: '#1e3a5f',
  tealDeep: '#0A7C8A',
  tealBright: '#0DCFCF',
  tealPale: '#E0F7F8',
  appBg: '#F7F9FC',
  mist: '#E8EDF2',
  gradNavy: 'linear-gradient(135deg, #1e3a5f 0%, #0A7C8A 100%)',
  gradTeal: 'linear-gradient(135deg, #0A7C8A 0%, #0DCFCF 100%)',
  fontBody: "'Sora', system-ui, -apple-system, sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', monospace"
};
const KEEP_TURNS = 3;
const CHART_COLORS = ['#0A7C8A', '#1e3a5f', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#0EA5E9', '#D97706'];
const TIER_ORDER = {
  basic: 0,
  starter: 1,
  pro: 2
};
const DEFAULT_SUGGESTIONS = ['Hi, tell me about how you can help', 'What can you see about my data?'];
function getMdStyles(T) {
  return `
    .ak-md p { margin: 0 0 6px; }
    .ak-md p:last-child { margin-bottom: 0; }
    .ak-md ul, .ak-md ol { margin: 4px 0 6px; padding-left: 18px; }
    .ak-md li { margin-bottom: 2px; }
    .ak-md strong { font-weight: 700; }
    .ak-md em { font-style: italic; }
    .ak-md h1, .ak-md h2, .ak-md h3 { font-weight: 700; margin: 8px 0 4px; font-size: 13px; }
    .ak-md code { font-family: ${T.fontMono}; background: rgba(0,0,0,0.07); border-radius: 4px; padding: 1px 4px; font-size: 12px; }
    .ak-md pre { background: rgba(0,0,0,0.07); border-radius: 6px; padding: 8px 10px; overflow-x: auto; margin: 4px 0; }
    .ak-md pre code { background: none; padding: 0; }
    .ak-md table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 12px; }
    .ak-md th, .ak-md td { border: 1px solid rgba(0,0,0,0.12); padding: 4px 8px; text-align: left; }
    .ak-md th { background: rgba(0,0,0,0.05); font-weight: 700; }
    .ak-md hr { border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 6px 0; }
    .ak-md blockquote { border-left: 3px solid rgba(0,0,0,0.15); margin: 4px 0; padding-left: 10px; color: #555; }
  `;
}

// ── Chart rendering ───────────────────────────────────────────────────────────

function fmtVal(v) {
  if (typeof v !== 'number') return v;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString();
}
function renderRechartsChart(spec) {
  const {
    type,
    data = [],
    xLabel,
    yLabel
  } = spec;
  const axisStyle = {
    fontSize: 11,
    fill: '#6B7280'
  };
  const gridStyle = {
    strokeDasharray: '3 3',
    stroke: '#F3F4F6'
  };
  const margin = {
    top: 8,
    right: 24,
    left: 8,
    bottom: xLabel ? 28 : 8
  };
  const tooltipFmt = v => [fmtVal(v)];
  if (type === 'bar') return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_recharts.BarChart, {
    data: data,
    margin: margin,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.CartesianGrid, {
      ...gridStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.XAxis, {
      dataKey: "label",
      tick: axisStyle,
      label: xLabel ? {
        value: xLabel,
        position: 'insideBottom',
        offset: -12,
        fontSize: 11,
        fill: '#9CA3AF'
      } : undefined
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.YAxis, {
      tick: axisStyle,
      tickFormatter: fmtVal,
      label: yLabel ? {
        value: yLabel,
        angle: -90,
        position: 'insideLeft',
        offset: 8,
        fontSize: 11,
        fill: '#9CA3AF'
      } : undefined,
      width: 50
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Tooltip, {
      formatter: tooltipFmt
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Bar, {
      dataKey: "value",
      radius: [4, 4, 0, 0],
      children: data.map((d, i) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Cell, {
        fill: d.color || CHART_COLORS[i % CHART_COLORS.length]
      }, i))
    })]
  });
  if (type === 'hbar') return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_recharts.BarChart, {
    data: data,
    layout: "vertical",
    margin: {
      top: 8,
      right: 48,
      left: 8,
      bottom: 8
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.CartesianGrid, {
      ...gridStyle,
      horizontal: false
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.XAxis, {
      type: "number",
      tick: axisStyle,
      tickFormatter: fmtVal,
      label: xLabel ? {
        value: xLabel,
        position: 'insideBottom',
        offset: -4,
        fontSize: 11,
        fill: '#9CA3AF'
      } : undefined
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.YAxis, {
      type: "category",
      dataKey: "label",
      width: 100,
      tick: {
        ...axisStyle,
        width: 95
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Tooltip, {
      formatter: tooltipFmt
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Bar, {
      dataKey: "value",
      radius: [0, 4, 4, 0],
      children: data.map((d, i) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Cell, {
        fill: d.color || CHART_COLORS[i % CHART_COLORS.length]
      }, i))
    })]
  });
  if (type === 'line') return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_recharts.LineChart, {
    data: data,
    margin: margin,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.CartesianGrid, {
      ...gridStyle
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.XAxis, {
      dataKey: "label",
      tick: axisStyle,
      label: xLabel ? {
        value: xLabel,
        position: 'insideBottom',
        offset: -12,
        fontSize: 11,
        fill: '#9CA3AF'
      } : undefined
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.YAxis, {
      tick: axisStyle,
      tickFormatter: fmtVal,
      label: yLabel ? {
        value: yLabel,
        angle: -90,
        position: 'insideLeft',
        offset: 8,
        fontSize: 11,
        fill: '#9CA3AF'
      } : undefined,
      width: 50
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Tooltip, {
      formatter: tooltipFmt
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Line, {
      type: "monotone",
      dataKey: "value",
      stroke: CHART_COLORS[0],
      strokeWidth: 2,
      dot: {
        r: 3,
        fill: CHART_COLORS[0]
      },
      activeDot: {
        r: 5
      }
    })]
  });
  if (type === 'pie') return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_recharts.PieChart, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Pie, {
      data: data,
      dataKey: "value",
      nameKey: "label",
      cx: "50%",
      cy: "48%",
      outerRadius: 95,
      innerRadius: 38,
      children: data.map((d, i) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Cell, {
        fill: d.color || CHART_COLORS[i % CHART_COLORS.length]
      }, i))
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Tooltip, {
      formatter: tooltipFmt
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.Legend, {
      iconSize: 10,
      wrapperStyle: {
        fontSize: 11
      }
    })]
  });
  return null;
}
function ChartBlock({
  specString,
  T
}) {
  const containerRef = (0, _react.useRef)(null);
  const [copied, setCopied] = (0, _react.useState)(false);
  const [copyFailed, setCopyFailed] = (0, _react.useState)(false);
  let spec = null,
    parseError = null;
  try {
    spec = JSON.parse(specString);
  } catch (e) {
    parseError = e.message;
  }
  const copyAsImage = (0, _react.useCallback)(() => {
    const svgEl = containerRef.current?.querySelector('.recharts-surface') ?? containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const title = spec?.title || '';
    const SCALE = 2;
    const titleH = title ? 36 : 0;
    const serialized = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([serialized], {
      type: 'image/svg+xml;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const r = svgEl.getBoundingClientRect();
      const w = Math.round(r.width) || 560,
        h = Math.round(r.height) || 260;
      const canvas = document.createElement('canvas');
      canvas.width = w * SCALE;
      canvas.height = (h + titleH) * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (title) {
        ctx.font = `bold ${13 * SCALE}px system-ui`;
        ctx.fillStyle = '#111827';
        ctx.textAlign = 'center';
        ctx.fillText(title, canvas.width / 2, Math.round(22 * SCALE));
      }
      ctx.drawImage(img, 0, 0, img.naturalWidth || w, img.naturalHeight || h, 0, titleH * SCALE, w * SCALE, h * SCALE);
      URL.revokeObjectURL(url);
      canvas.toBlob(async pngBlob => {
        if (!pngBlob) {
          setCopyFailed(true);
          setTimeout(() => setCopyFailed(false), 2000);
          return;
        }
        try {
          await navigator.clipboard.write([new ClipboardItem({
            'image/png': pngBlob
          })]);
        } catch {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(pngBlob);
          a.download = 'chart.png';
          a.click();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2000);
    };
    img.src = url;
  }, [spec]);
  if (parseError || !spec) return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      margin: '6px 0 2px',
      padding: '10px 12px',
      border: '1.5px solid #FECACA',
      borderRadius: 10,
      background: '#FFF5F5'
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
      style: {
        fontSize: 11,
        color: '#EF4444'
      },
      children: ["Chart error: ", parseError || 'Invalid spec']
    })
  });
  const chart = renderRechartsChart(spec);
  const chartH = spec.type === 'pie' ? 300 : 260;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      margin: '6px 0 2px',
      border: `1.5px solid ${T.mist}`,
      borderRadius: 10,
      overflow: 'hidden',
      background: '#fff',
      paddingTop: 10
    },
    children: [spec.title && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 700,
        color: '#111827',
        paddingBottom: 4
      },
      children: spec.title
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      ref: containerRef,
      style: {
        width: '100%'
      },
      children: chart ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_recharts.ResponsiveContainer, {
        width: "100%",
        height: chartH,
        children: chart
      }) : /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          padding: 20,
          color: '#9CA3AF',
          fontSize: 12
        },
        children: ["Unknown chart type: ", spec.type]
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        padding: '4px 10px 6px',
        borderTop: `1px solid ${T.mist}`,
        display: 'flex',
        justifyContent: 'flex-end'
      },
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("button", {
        onClick: copyAsImage,
        title: "Copy chart as PNG",
        style: {
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 5px',
          borderRadius: 6,
          color: copyFailed ? '#EF4444' : copied ? T.tealDeep : '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 600
        },
        children: [copied ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Check, {
          size: 13
        }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Copy, {
          size: 13
        }), copyFailed ? 'Failed' : copied ? 'Copied' : 'Copy image']
      })
    })]
  });
}

// ── Markdown helpers ──────────────────────────────────────────────────────────

function mdToPlain(md) {
  let t = md;
  t = t.replace(/```chart[\s\S]*?```/gi, m => {
    try {
      const s = JSON.parse(m.replace(/^```chart\s*/i, '').replace(/```$/, ''));
      return `[Chart: ${s.title || s.type}]`;
    } catch {
      return '[Chart]';
    }
  });
  t = t.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => code.trim());
  t = t.replace(/^#{1,6}\s+/gm, '');
  t = t.replace(/\*\*\*(.+?)\*\*\*/g, '$1').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');
  t = t.replace(/__(.+?)__/g, '$1').replace(/_(.+?)_/g, '$1').replace(/`(.+?)`/g, '$1');
  t = t.replace(/\[(.+?)\]\(.+?\)/g, '$1');
  t = t.replace(/^\|[-| :]+\|$/gm, '');
  t = t.replace(/^\|(.+)\|$/gm, (_, cells) => cells.split('|').map(c => c.trim()).filter(Boolean).join('   '));
  t = t.replace(/^[ \t]*[-*+]\s+/gm, '• ').replace(/^>\s*/gm, '').replace(/^[-*_]{3,}$/gm, '');
  t = t.replace(/\n{3,}/g, '\n\n');
  return t.trim();
}
function CopyButtons({
  text,
  T
}) {
  const [copiedPlain, setCopiedPlain] = (0, _react.useState)(false);
  const [copiedMd, setCopiedMd] = (0, _react.useState)(false);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 6,
      alignSelf: 'flex-end'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("button", {
      onClick: () => navigator.clipboard.writeText(mdToPlain(text)).then(() => {
        setCopiedPlain(true);
        setTimeout(() => setCopiedPlain(false), 1500);
      }),
      style: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 5px',
        borderRadius: 6,
        color: copiedPlain ? T.tealDeep : '#9CA3AF',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600
      },
      children: [copiedPlain ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Check, {
        size: 13
      }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Copy, {
        size: 13
      }), " ", copiedPlain ? 'Copied' : 'Copy']
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("button", {
      onClick: () => navigator.clipboard.writeText(text).then(() => {
        setCopiedMd(true);
        setTimeout(() => setCopiedMd(false), 1500);
      }),
      style: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 4px',
        borderRadius: 6,
        color: copiedMd ? T.tealDeep : '#C8C8C8',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 10,
        fontWeight: 500
      },
      children: [copiedMd ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Check, {
        size: 11
      }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Copy, {
        size: 11
      }), " ", copiedMd ? 'Copied' : 'MD']
    })]
  });
}
function Message({
  role,
  content,
  T
}) {
  const isUser = role === 'user';
  const makeMdComponents = T => ({
    a: ({
      href,
      children
    }) => /*#__PURE__*/(0, _jsxRuntime.jsx)("a", {
      href: href,
      target: "_blank",
      rel: "noopener noreferrer",
      children: children
    }),
    code({
      inline,
      className,
      children
    }) {
      if (!inline && /language-chart/i.test(className || '')) return /*#__PURE__*/(0, _jsxRuntime.jsx)(ChartBlock, {
        specString: String(children).trim(),
        T: T
      });
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("code", {
        className: className,
        children: children
      });
    },
    pre({
      children
    }) {
      const child = Array.isArray(children) ? children[0] : children;
      if (child?.type === ChartBlock) return /*#__PURE__*/(0, _jsxRuntime.jsx)(_jsxRuntime.Fragment, {
        children: children
      });
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("pre", {
        children: children
      });
    }
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 10,
      padding: '0 4px'
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        maxWidth: '82%',
        display: 'flex',
        flexDirection: 'column'
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          padding: '10px 13px',
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isUser ? T.gradTeal : '#fff',
          color: isUser ? '#fff' : '#111',
          fontSize: 13,
          lineHeight: 1.55,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          wordBreak: 'break-word',
          ...(isUser ? {
            whiteSpace: 'pre-wrap'
          } : {})
        },
        children: isUser ? content : /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          className: "ak-md",
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactMarkdown.default, {
            remarkPlugins: [_remarkGfm.default],
            components: makeMdComponents(T),
            children: content
          })
        })
      }), !isUser && /*#__PURE__*/(0, _jsxRuntime.jsx)(CopyButtons, {
        text: content,
        T: T
      })]
    })
  });
}
function CondensedDivider() {
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      margin: '6px 4px 2px',
      color: '#9CA3AF'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        height: 1,
        background: '#E5E7EB'
      }
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
      style: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase'
      },
      children: "Context condensed"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        flex: 1,
        height: 1,
        background: '#E5E7EB'
      }
    })]
  });
}
function SummaryCard({
  content,
  T
}) {
  const makeMdComponents = T => ({
    a: ({
      href,
      children
    }) => /*#__PURE__*/(0, _jsxRuntime.jsx)("a", {
      href: href,
      target: "_blank",
      rel: "noopener noreferrer",
      children: children
    }),
    code({
      inline,
      className,
      children
    }) {
      if (!inline && /language-chart/i.test(className || '')) return /*#__PURE__*/(0, _jsxRuntime.jsx)(ChartBlock, {
        specString: String(children).trim(),
        T: T
      });
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("code", {
        className: className,
        children: children
      });
    }
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("details", {
    style: {
      margin: '2px 4px 10px',
      background: '#fff',
      border: `1.5px solid ${T.mist}`,
      borderRadius: 10
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("summary", {
      style: {
        padding: '8px 12px',
        background: T.mist,
        borderRadius: '8px 8px 0 0',
        fontSize: 11,
        fontWeight: 700,
        color: T.tealDeep,
        cursor: 'pointer',
        userSelect: 'none'
      },
      children: "Summary of earlier conversation"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        padding: '8px 12px 10px'
      },
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: "ak-md",
        style: {
          fontSize: 12,
          color: '#374151'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactMarkdown.default, {
          remarkPlugins: [_remarkGfm.default],
          components: makeMdComponents(T),
          children: content
        })
      })
    })]
  });
}
function ThinkingDots({
  T
}) {
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    style: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: 10,
      padding: '0 4px'
    },
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        padding: '10px 16px',
        borderRadius: '16px 16px 16px 4px',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        gap: 5,
        alignItems: 'center'
      },
      children: [0, 1, 2].map(i => /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: T.tealDeep,
          animation: `ak-dot 1.2s ease-in-out ${i * 0.2}s infinite`
        }
      }, i))
    })
  });
}
function ModelPickerOverlay({
  catalogue,
  currentModelId,
  userTier,
  onSelect,
  onClose,
  T
}) {
  const userTierRank = TIER_ORDER[userTier] ?? 0;
  const enabled = catalogue.filter(m => m.enabled !== false);
  const TIER_LABELS = {
    starter: 'Standard+',
    pro: 'Pro only'
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    style: {
      borderTop: `1.5px solid ${T.tealPale}`,
      background: '#fff',
      padding: '12px 12px 4px',
      flexShrink: 0,
      maxHeight: 320,
      overflowY: 'auto'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        style: {
          fontSize: 12,
          fontWeight: 700,
          color: '#374151'
        },
        children: "Choose AI model"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
        onClick: onClose,
        style: {
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#9CA3AF',
          padding: '2px 4px',
          display: 'flex'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.X, {
          size: 15
        })
      })]
    }), enabled.length === 0 && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        fontSize: 12,
        color: '#9CA3AF',
        textAlign: 'center',
        padding: '12px 0 8px'
      },
      children: "No models configured."
    }), enabled.map(entry => {
      const locked = userTierRank < (entry.minTier ? TIER_ORDER[entry.minTier] ?? 0 : 0);
      const selected = currentModelId ? currentModelId === entry.id : enabled.indexOf(entry) === 0;
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        onClick: () => !locked && onSelect(entry),
        style: {
          padding: '9px 11px',
          borderRadius: 9,
          border: `1.5px solid ${selected ? T.tealDeep : '#E5E7EB'}`,
          marginBottom: 7,
          cursor: locked ? 'default' : 'pointer',
          opacity: locked ? 0.45 : 1,
          background: selected ? T.tealPale : '#FAFAFA'
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: entry.description ? 3 : 0
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
            style: {
              fontSize: 13,
              fontWeight: 700,
              color: locked ? '#9CA3AF' : '#111',
              display: 'flex',
              alignItems: 'center',
              gap: 5
            },
            children: [selected && /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Check, {
              size: 12,
              color: T.tealDeep
            }), entry.displayName || entry.id]
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
            style: {
              display: 'flex',
              gap: 5,
              alignItems: 'center'
            },
            children: [entry.factor != null && /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
              style: {
                fontSize: 10,
                color: '#9CA3AF'
              },
              children: [entry.factor, "\xD7 quota"]
            }), entry.minTier && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
              style: {
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: 4,
                background: '#F3F4F6',
                color: '#6B7280'
              },
              children: TIER_LABELS[entry.minTier] ?? entry.minTier
            })]
          })]
        }), entry.description && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            fontSize: 11,
            color: locked ? '#C9CDD4' : '#6B7280',
            lineHeight: 1.4
          },
          children: entry.description
        })]
      }, entry.id);
    })]
  });
}

// ── Usage formatting ──────────────────────────────────────────────────────────

const fmtN = n => (n ?? 0).toLocaleString();
const fmt = n => n == null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
function nextResetDate(type) {
  const d = new Date();
  if (type === 'week') {
    const days = d.getDay() === 0 ? 1 : 8 - d.getDay();
    d.setDate(d.getDate() + days);
  } else {
    d.setMonth(d.getMonth() + 1, 1);
  }
  d.setHours(0, 0, 0, 0);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });
}
function buildUsageText(usage, limits) {
  if (!usage) return 'No usage data available yet.';
  const lim = (v, m) => m == null || m === -1 ? fmtN(v) : `${fmtN(v)} / ${fmtN(m)}`;
  const pct = (v, m) => m != null && m > 0 ? ` *(${Math.min(100, Math.round(v / m * 100))}%)*` : '';
  return `**Usage Statistics**

**This conversation**
- Prompt tokens: ${fmtN(usage.convTokensIn)}
- Completion tokens: ${fmtN(usage.convTokensOut)}
- Total: ${fmtN((usage.convTokensIn ?? 0) + (usage.convTokensOut ?? 0))}${usage.tokensIn != null ? `\n- Last prompt size: ${fmtN(usage.tokensIn)} tokens` : ''}${limits?.aiContextLen != null ? `\n- Context limit: ${fmtN(limits.aiContextLen)} tokens` : ''}

**This week** *(resets ${nextResetDate('week')})*
- Queries: ${lim(usage.queriesThisWeek, limits?.aiQueriesPerWeek)}${pct(usage.queriesThisWeek, limits?.aiQueriesPerWeek)}
- Prompt tokens: ${lim(usage.tokensInThisWeek, limits?.aiTokensInPerWeek)}${pct(usage.tokensInThisWeek, limits?.aiTokensInPerWeek)}
- Completion tokens: ${lim(usage.tokensOutThisWeek, limits?.aiTokensOutPerWeek)}${pct(usage.tokensOutThisWeek, limits?.aiTokensOutPerWeek)}${usage.toolCallsThisWeek != null ? `\n- Tool calls: ${lim(usage.toolCallsThisWeek, limits?.aiToolCallsPerWeek)}` : ''}

**This month** *(resets ${nextResetDate('month')})*
- Queries: ${fmtN(usage.queriesThisMonth)}
- Prompt tokens: ${fmtN(usage.tokensInThisMonth)}
- Completion tokens: ${fmtN(usage.tokensOutThisMonth)}${usage.toolCallsThisMonth != null ? `\n- Tool calls: ${fmtN(usage.toolCallsThisMonth)}` : ''}

*Tokens sent includes your question plus the full conversation history each turn. Tool calls are weighted by complexity (0.5–3.0 units each).*`;
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Generic AI chat panel.
 *
 * @param {object}   props
 * @param {function} props.onClose
 * @param {object[]} props.messages        OpenAI-format message array with optional `ephemeral` / `condensed` flags.
 * @param {object}   props.usage           Usage counters (queriesThisWeek, tokensIn, etc.)
 * @param {object}   props.limits          Tier limits (aiQueriesPerWeek, aiTokensInPerWeek, etc.)
 * @param {function} props.onNewMessage    (msg) => void
 * @param {function} props.onReply         (data) => void  — called with full AI response data
 * @param {function} props.onClear         () => void
 * @param {function} props.onCondense      (condensedHistory, prevTokensIn) => void
 * @param {function} props.onAsk           async (message, history, prevTokensIn) => { reply, ... }
 * @param {function} props.onCondenseHistory  async (history) => { condensedHistory }
 * @param {string}   [props.displayName]   Header display name.
 * @param {string}   [props.subtitle]      Header subtitle.
 * @param {React.ReactNode} [props.icon]   Header icon node.
 * @param {string[]} [props.suggestions]   Suggestion chips shown on empty state.
 * @param {string}   [props.helpText]      Markdown for /help command.
 * @param {string}   [props.samplesText]   Markdown for /samples command.
 * @param {function} [props.loadCatalogue] async () => model[]
 * @param {function} [props.loadModelPref] async (uid) => modelId
 * @param {function} [props.saveModelPref] async (uid, modelId) => void
 * @param {string}   [props.userUid]
 * @param {string}   [props.userTier='basic']
 * @param {number}   [props.memoryCount=0]
 * @param {number}   [props.pendingCount=0]
 * @param {function} [props.onOpenSettings] () => void
 * @param {React.ReactNode} [props.settingsSlot]  Rendered when settings icon is clicked (overlay).
 * @param {object}   [props.theme]         Partial theme override (merged with DEFAULT_THEME).
 * @param {number}   [props.windowWidth]   Optional — pass window width for responsive behaviour.
 */
function AIChatPanel({
  onClose,
  messages,
  usage,
  limits,
  onNewMessage,
  onReply,
  onClear,
  onCondense,
  onAsk,
  onCondenseHistory,
  displayName = 'AI Assistant',
  subtitle = 'Ask me anything',
  icon,
  suggestions = DEFAULT_SUGGESTIONS,
  helpText,
  samplesText,
  loadCatalogue,
  loadModelPref,
  saveModelPref,
  userUid,
  userTier = 'basic',
  memoryCount = 0,
  pendingCount = 0,
  onOpenSettings,
  settingsSlot,
  theme: themeProp,
  windowWidth: windowWidthProp
}) {
  const T = {
    ...DEFAULT_THEME,
    ...themeProp
  };
  const [input, setInput] = (0, _react.useState)('');
  const [thinking, setThinking] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(null);
  const [pendingMsg, setPendingMsg] = (0, _react.useState)(null);
  const [docked, setDocked] = (0, _react.useState)(true);
  const [floatPos, setFloatPos] = (0, _react.useState)({
    x: 0,
    y: 0
  });
  const [catalogue, setCatalogue] = (0, _react.useState)([]);
  const [currentModelId, setCurrentModelId] = (0, _react.useState)(null);
  const [showModelPicker, setShowModelPicker] = (0, _react.useState)(false);
  const [showSettings, setShowSettings] = (0, _react.useState)(false);
  const messagesEndRef = (0, _react.useRef)(null);
  const inputRef = (0, _react.useRef)(null);
  const queryHistory = (0, _react.useRef)([]);
  const historyIdx = (0, _react.useRef)(-1);
  const draftInput = (0, _react.useRef)('');
  const floatRef = (0, _react.useRef)(null);
  const dragging = (0, _react.useRef)(false);
  const dragOffset = (0, _react.useRef)({
    x: 0,
    y: 0
  });
  const windowWidth = windowWidthProp ?? (typeof window !== 'undefined' ? window.innerWidth : 800);
  (0, _react.useEffect)(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, thinking, pendingMsg]);
  (0, _react.useEffect)(() => {
    inputRef.current?.focus();
  }, []);
  (0, _react.useEffect)(() => {
    if (!userUid || !loadCatalogue) return;
    Promise.all([loadCatalogue(), loadModelPref ? loadModelPref(userUid) : Promise.resolve(null)]).then(([cat, pref]) => {
      setCatalogue(cat ?? []);
      setCurrentModelId(pref);
    }).catch(() => {});
  }, [userUid, loadCatalogue, loadModelPref]);
  const historyForApi = (() => {
    const nonEphemeral = messages.filter(m => !m.ephemeral);
    return [...nonEphemeral.filter(m => m.condensed), ...nonEphemeral.filter(m => !m.condensed)].map(({
      role,
      content
    }) => ({
      role,
      content
    }));
  })();
  const completedTurns = Math.floor(messages.filter(m => !m.ephemeral).length / 2);
  const HELP = helpText || `**Available commands**

| Command | Description |
|---|---|
| \`/clear\` | Clear the conversation |
| \`/condense\` | Summarise older context, keep last 3 turns |
| \`/show_summary\` | Show the last condensed summary |
| \`/help\` | Show this message |
| \`/usage\` | Show token and query usage statistics |
| \`/samples\` | Show sample questions |
| \`/model\` | Choose the AI model |`;
  const SAMPLES = samplesText || 'Try asking me questions about your data.';
  const send = async text => {
    const raw = text.trim();
    if (!raw || thinking) return;
    const msg = raw.startsWith('/') ? raw.toLowerCase() : raw;
    setInput('');
    setError(null);
    queryHistory.current = [...queryHistory.current, raw];
    historyIdx.current = -1;
    draftInput.current = '';
    if (msg === '/clear') {
      onClear();
      return;
    }
    if (msg === '/memory') {
      if (onOpenSettings) onOpenSettings('memory');
      return;
    }
    if (msg === '/help') {
      onNewMessage({
        role: 'user',
        content: '/help',
        ephemeral: true
      });
      onNewMessage({
        role: 'assistant',
        content: HELP,
        ephemeral: true
      });
      return;
    }
    if (msg === '/usage') {
      onNewMessage({
        role: 'user',
        content: '/usage',
        ephemeral: true
      });
      onNewMessage({
        role: 'assistant',
        content: buildUsageText(usage, limits),
        ephemeral: true
      });
      return;
    }
    if (msg === '/samples') {
      onNewMessage({
        role: 'user',
        content: '/samples',
        ephemeral: true
      });
      onNewMessage({
        role: 'assistant',
        content: SAMPLES,
        ephemeral: true
      });
      return;
    }
    if (msg === '/show_summary') {
      const summaryMsg = messages.find(m => m.condensed && m.role === 'assistant');
      const before = usage?.condenseTokensBefore,
        after = usage?.condenseTokensAfter;
      const tokenLine = before != null ? `\n\n*Tokens — before: ${fmtN(before)}${after != null ? `, after: ${fmtN(after)}` : ' (manual condense)'}*` : '';
      onNewMessage({
        role: 'user',
        content: '/show_summary',
        ephemeral: true
      });
      onNewMessage({
        role: 'assistant',
        content: summaryMsg ? `**Last condensed summary:**\n\n${summaryMsg.content}${tokenLine}` : 'No summary yet. Use `/condense` to create one.',
        ephemeral: true
      });
      return;
    }
    if (msg === '/model' || msg.startsWith('/model ')) {
      const modelArg = msg.length > 6 ? msg.slice(7).trim() : '';
      if (!modelArg) {
        setShowModelPicker(true);
        return;
      }
      const entry = catalogue.filter(m => m.enabled !== false).find(m => m.id === modelArg);
      if (!entry) {
        onNewMessage({
          role: 'user',
          content: raw,
          ephemeral: true
        });
        onNewMessage({
          role: 'assistant',
          content: `Model \`${modelArg}\` not found. Use \`/model\` to see options.`,
          ephemeral: true
        });
        return;
      }
      const locked = (TIER_ORDER[userTier] ?? 0) < (entry.minTier ? TIER_ORDER[entry.minTier] ?? 0 : 0);
      if (locked) {
        onNewMessage({
          role: 'user',
          content: raw,
          ephemeral: true
        });
        onNewMessage({
          role: 'assistant',
          content: `**${entry.displayName || entry.id}** requires a higher plan.`,
          ephemeral: true
        });
        return;
      }
      if (userUid && saveModelPref) saveModelPref(userUid, entry.id).catch(() => {});
      setCurrentModelId(entry.id);
      onNewMessage({
        role: 'user',
        content: raw,
        ephemeral: true
      });
      onNewMessage({
        role: 'assistant',
        content: `Switched to **${entry.displayName || entry.id}**.`,
        ephemeral: true
      });
      return;
    }
    if (msg === '/condense') {
      if (historyForApi.length <= KEEP_TURNS * 2) {
        onNewMessage({
          role: 'user',
          content: '/condense',
          ephemeral: true
        });
        onNewMessage({
          role: 'assistant',
          content: 'Not enough history to condense.',
          ephemeral: true
        });
        return;
      }
      if (!onCondenseHistory) {
        onNewMessage({
          role: 'user',
          content: '/condense',
          ephemeral: true
        });
        onNewMessage({
          role: 'assistant',
          content: 'Condense is not available.',
          ephemeral: true
        });
        return;
      }
      setThinking(true);
      try {
        const data = await onCondenseHistory(historyForApi);
        if (data.condensedHistory) {
          const summarisedTurns = Math.floor((historyForApi.length - KEEP_TURNS * 2) / 2);
          const keptTurns = Math.min(KEEP_TURNS, Math.floor(historyForApi.length / 2));
          onCondense(data.condensedHistory, usage?.tokensIn ?? null);
          onNewMessage({
            role: 'user',
            content: '/condense',
            ephemeral: true
          });
          onNewMessage({
            role: 'assistant',
            content: `Condensed ${summarisedTurns} older turn${summarisedTurns !== 1 ? 's' : ''} into a summary. Last ${keptTurns} turn${keptTurns !== 1 ? 's' : ''} kept in full.`,
            ephemeral: true
          });
        } else {
          onNewMessage({
            role: 'user',
            content: '/condense',
            ephemeral: true
          });
          onNewMessage({
            role: 'assistant',
            content: 'Nothing old enough to condense.',
            ephemeral: true
          });
        }
      } catch (err) {
        onNewMessage({
          role: 'user',
          content: '/condense',
          ephemeral: true
        });
        onNewMessage({
          role: 'assistant',
          content: err?.message || 'Condense failed.',
          ephemeral: true
        });
      } finally {
        setThinking(false);
      }
      return;
    }
    setPendingMsg(msg);
    setThinking(true);
    try {
      const data = await onAsk(msg, historyForApi, usage?.tokensIn ?? 0);
      setPendingMsg(null);
      if (data.condensedHistory) {
        onReply({
          ...data,
          userMsg: msg
        });
      } else {
        onNewMessage({
          role: 'user',
          content: msg
        });
        onNewMessage({
          role: 'assistant',
          content: data.reply
        });
        onReply(data);
      }
    } catch (err) {
      setPendingMsg(null);
      const code = err?.code;
      if (code === 'functions/permission-denied') setError('This feature requires a higher plan. Upgrade in Settings.');else if (code === 'functions/resource-exhausted') setError((err?.message || 'Limit reached.') + ' Upgrade your plan for higher limits.');else setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setThinking(false);
    }
  };
  const undock = () => {
    const pw = 420,
      ph = 600;
    setFloatPos({
      x: Math.max(0, window.innerWidth - pw - 20),
      y: Math.max(0, Math.floor(window.innerHeight / 4))
    });
    setDocked(false);
  };
  const handleDragStart = e => {
    if (e.target.closest('button') || docked) return;
    e.preventDefault();
    dragging.current = true;
    dragOffset.current = {
      x: e.clientX - floatPos.x,
      y: e.clientY - floatPos.y
    };
  };
  (0, _react.useEffect)(() => {
    if (docked) return;
    const onMove = e => {
      if (!dragging.current) return;
      const pw = floatRef.current?.offsetWidth ?? 420,
        ph = floatRef.current?.offsetHeight ?? 600;
      setFloatPos({
        x: Math.max(0, Math.min(window.innerWidth - pw, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - ph, e.clientY - dragOffset.current.y))
      });
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [docked]);
  const isEmpty = messages.length === 0 && !thinking && !pendingMsg;
  const hasMessages = messages.length > 0 || !!pendingMsg;
  const badgeCount = memoryCount + pendingCount;
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("style", {
      children: `
        @keyframes ak-dot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.4; } 30% { transform: translateY(-4px); opacity: 1; } }
        @keyframes ak-slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes ak-float-in { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        ${getMdStyles(T)}
      `
    }), docked && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1000,
        backdropFilter: 'blur(3px)'
      },
      onClick: onClose
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      ref: floatRef,
      style: docked ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '88vh',
        maxWidth: '95vw',
        margin: '0 auto',
        background: T.appBg,
        borderRadius: '20px 20px 0 0',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        animation: 'ak-slideUp 0.3s ease-out',
        overflow: 'hidden'
      } : {
        position: 'fixed',
        left: floatPos.x,
        top: floatPos.y,
        width: 420,
        height: 600,
        minWidth: 320,
        minHeight: 400,
        resize: 'both',
        overflow: 'hidden',
        borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        background: T.appBg,
        animation: 'ak-float-in 0.25s ease-out'
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        onMouseDown: handleDragStart,
        style: {
          background: T.gradNavy,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          cursor: docked ? 'default' : 'move',
          userSelect: 'none'
        },
        children: [icon && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            flexShrink: 0
          },
          children: icon
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            flex: 1
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              color: '#fff',
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: -0.3
            },
            children: displayName
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              color: 'rgba(255,255,255,0.5)',
              fontSize: 11,
              marginTop: 1
            },
            children: subtitle
          })]
        }), (onOpenSettings || settingsSlot) && /*#__PURE__*/(0, _jsxRuntime.jsxs)("button", {
          onClick: () => {
            if (onOpenSettings) onOpenSettings('memory');else setShowSettings(true);
          },
          title: "Settings",
          style: {
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '6px 8px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginRight: 2,
            position: 'relative'
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Brain, {
            size: 16
          }), badgeCount > 0 && /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
            style: {
              fontSize: 9,
              fontWeight: 800,
              background: pendingCount > 0 ? '#F59E0B' : T.tealBright,
              color: '#fff',
              borderRadius: 8,
              padding: '1px 4px',
              lineHeight: 1.4
            },
            children: badgeCount
          })]
        }), hasMessages && /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          onClick: onClear,
          title: "New chat",
          style: {
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '6px 8px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)',
            display: 'flex',
            marginRight: 2
          },
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.RotateCcw, {
            size: 16
          })
        }), windowWidth >= 768 && /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          onClick: docked ? undock : () => setDocked(true),
          title: docked ? 'Float panel' : 'Dock panel',
          style: {
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '6px 8px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)',
            display: 'flex',
            marginRight: 2
          },
          children: docked ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Maximize2, {
            size: 16
          }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Minimize2, {
            size: 16
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
          onClick: onClose,
          style: {
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: 8,
            padding: '6px 8px',
            cursor: 'pointer',
            color: '#fff',
            display: 'flex'
          },
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.X, {
            size: 18
          })
        })]
      }), showSettings && settingsSlot, /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 12px 8px',
          display: 'flex',
          flexDirection: 'column'
        },
        children: [isEmpty && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '0 16px'
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              background: T.tealPale,
              borderRadius: '50%',
              padding: 14
            },
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Sparkles, {
              size: 28,
              color: T.tealDeep
            })
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
            style: {
              textAlign: 'center'
            },
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              style: {
                fontWeight: 800,
                fontSize: 15,
                color: '#111',
                marginBottom: 4
              },
              children: displayName
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              style: {
                fontSize: 12,
                color: '#6B7280',
                lineHeight: 1.5
              },
              children: subtitle
            })]
          }), usage && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
            style: {
              width: '100%',
              background: '#fff',
              border: `1px solid ${T.tealPale}`,
              borderRadius: 10,
              padding: '10px 13px'
            },
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              style: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 10
              },
              children: /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
                style: {
                  fontSize: 10,
                  fontWeight: 800,
                  color: '#9CA3AF',
                  letterSpacing: 0.8,
                  textTransform: 'uppercase'
                },
                children: "This week's usage"
              })
            }), [{
              label: 'Queries',
              used: usage.queriesThisWeek ?? 0,
              max: limits?.aiQueriesPerWeek
            }, {
              label: 'Prompt tokens',
              used: usage.tokensInThisWeek ?? 0,
              max: limits?.aiTokensInPerWeek
            }, {
              label: 'Completion tokens',
              used: usage.tokensOutThisWeek ?? 0,
              max: limits?.aiTokensOutPerWeek
            }, {
              label: 'Tool calls',
              used: usage.toolCallsThisWeek ?? 0,
              max: limits?.aiToolCallsPerWeek
            }].map(({
              label,
              used,
              max
            }) => {
              const unlimited = max == null || max === -1;
              const pct = unlimited ? 0 : Math.min(1, used / max);
              const color = unlimited || pct < 0.6 ? '#10B981' : pct < 0.85 ? '#F59E0B' : '#EF4444';
              return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
                style: {
                  marginBottom: 9
                },
                children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
                  style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4
                  },
                  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
                    style: {
                      fontSize: 11,
                      color: '#6B7280'
                    },
                    children: label
                  }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
                    style: {
                      fontSize: 12,
                      fontWeight: 700,
                      color
                    },
                    children: [fmt(used), unlimited ? '' : ` / ${fmt(max)}`]
                  })]
                }), !unlimited && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
                  style: {
                    height: 3,
                    background: '#F3F4F6',
                    borderRadius: 2
                  },
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
                    style: {
                      height: 3,
                      width: `${pct * 100}%`,
                      background: color,
                      borderRadius: 2
                    }
                  })
                })]
              }, label);
            })]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            style: {
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
              width: '100%'
            },
            children: suggestions.map(s => /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
              onClick: () => send(s),
              style: {
                background: '#fff',
                border: `1.5px solid ${T.tealPale}`,
                borderRadius: 10,
                padding: '10px 13px',
                fontSize: 12,
                color: T.tealDeep,
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
              },
              children: s
            }, s))
          })]
        }), messages.map((m, i) => {
          if (m.condensed && m.role === 'user') return /*#__PURE__*/(0, _jsxRuntime.jsx)(CondensedDivider, {}, i);
          if (m.condensed && m.role === 'assistant') return /*#__PURE__*/(0, _jsxRuntime.jsx)(SummaryCard, {
            content: m.content,
            T: T
          }, i);
          return /*#__PURE__*/(0, _jsxRuntime.jsx)(Message, {
            role: m.role,
            content: m.content,
            T: T
          }, i);
        }), pendingMsg && /*#__PURE__*/(0, _jsxRuntime.jsx)(Message, {
          role: "user",
          content: pendingMsg,
          T: T
        }), thinking && /*#__PURE__*/(0, _jsxRuntime.jsx)(ThinkingDots, {
          T: T
        }), error && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          style: {
            margin: '4px 4px 8px',
            padding: '10px 13px',
            background: '#FFF5F5',
            border: '1px solid #FCA5A5',
            borderRadius: 10,
            fontSize: 12,
            color: '#DC2626',
            lineHeight: 1.5
          },
          children: error
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          ref: messagesEndRef
        })]
      }), hasMessages && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        style: {
          padding: '5px 14px',
          borderTop: '1px solid #F3F4F6',
          background: '#FAFAFA',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        },
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
          style: {
            fontSize: 10,
            color: '#9CA3AF',
            fontWeight: 700,
            fontFamily: T.fontMono,
            flexShrink: 0
          },
          children: ["Turn ", completedTurns]
        }), usage?.tokensIn != null && /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
          style: {
            fontSize: 10,
            color: '#096274',
            fontFamily: T.fontMono,
            flexShrink: 0
          },
          children: ["last \u2191", fmt(usage.tokensIn), " \u2193", fmt(usage.tokensOut)]
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
          style: {
            fontSize: 10,
            color: '#096274',
            fontFamily: T.fontMono,
            flexShrink: 0
          },
          children: ["conv \u2191", fmt(usage?.convTokensIn), " \u2193", fmt(usage?.convTokensOut)]
        })]
      }), showModelPicker && /*#__PURE__*/(0, _jsxRuntime.jsx)(ModelPickerOverlay, {
        catalogue: catalogue,
        currentModelId: currentModelId,
        userTier: userTier,
        T: T,
        onSelect: async entry => {
          if (userUid && saveModelPref) {
            await saveModelPref(userUid, entry.id).catch(() => {});
            setCurrentModelId(entry.id);
          }
          setShowModelPicker(false);
        },
        onClose: () => setShowModelPicker(false)
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("form", {
        onSubmit: e => {
          e.preventDefault();
          send(input);
        },
        style: {
          padding: '10px 12px 16px',
          borderTop: showModelPicker ? 'none' : '1px solid #E5E7EB',
          background: '#fff',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        },
        children: [currentModelId && catalogue.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            paddingLeft: 2
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
            style: {
              fontSize: 10,
              fontWeight: 600,
              color: T.tealDeep
            },
            children: ["Model: ", catalogue.find(m => m.id === currentModelId)?.displayName ?? currentModelId]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
            type: "button",
            onClick: () => {
              if (userUid && saveModelPref) saveModelPref(userUid, null).catch(() => {});
              setCurrentModelId(null);
            },
            style: {
              fontSize: 10,
              color: '#9CA3AF',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            },
            children: "\xD7 reset"
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          style: {
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end'
          },
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("textarea", {
            ref: inputRef,
            value: input,
            onChange: e => setInput(e.target.value),
            onKeyDown: e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
                return;
              }
              const hist = queryHistory.current;
              if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && hist.length > 0) {
                e.preventDefault();
                let idx = historyIdx.current;
                if (e.key === 'ArrowUp') {
                  if (idx === -1) draftInput.current = input;
                  idx = idx === -1 ? hist.length - 1 : Math.max(0, idx - 1);
                } else {
                  idx = idx === -1 ? -1 : idx + 1;
                  if (idx >= hist.length) idx = -1;
                }
                historyIdx.current = idx;
                setInput(idx === -1 ? draftInput.current : hist[idx]);
              }
            },
            placeholder: "Ask me anything\u2026",
            rows: 1,
            disabled: thinking,
            style: {
              flex: 1,
              padding: '10px 12px',
              borderRadius: 12,
              border: `1.5px solid ${input.trim() ? T.tealDeep : '#E5E7EB'}`,
              fontSize: 13,
              fontFamily: T.fontBody,
              outline: 'none',
              resize: 'none',
              lineHeight: 1.45,
              maxHeight: 100,
              overflowY: 'auto',
              transition: 'border-color 0.15s',
              background: thinking ? '#F9FAFB' : '#fff'
            }
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
            type: "submit",
            disabled: !input.trim() || thinking,
            style: {
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 12,
              border: 'none',
              background: !input.trim() || thinking ? '#E5E7EB' : T.gradTeal,
              color: '#fff',
              cursor: !input.trim() || thinking ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s'
            },
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_lucideReact.Send, {
              size: 16
            })
          })]
        })]
      })]
    })]
  });
}